import { bffInstance } from "../api/axiosInstance";
import {
  useQuery,
  useQueryClient,
  useMutation,
} from "react-query";
import { useAdoptEaseStore } from "./useAdoptEaseStore";
import { IAdoptEaseCampaign } from "./AdoptEaseCampaignInterface";

export interface IAdoptEaseConfig {
  workflow: string;
  application: string;
  usageCheck?: string;
  reminderDuration: string;
  isActive: boolean;
  appId?: string;
  courseId?: string[];
  message?: string;
  messageTraining?: string;
  workflowHelpLink?: string;
  appHelpLink?: string;
}

export interface IAdoptEaseUsageItem {
  workflow: string;
  remindedOn: Date;
}

export interface IAdoptEaseUsage {
  loginId: string;
  usage: IAdoptEaseUsageItem[];
}


export const updateUserAdoptEaseUsage = async (workflowDetails: Record<string, Date>) => {
  const res = await bffInstance.patch<{ data: string }>(`/adoptease/updateUserAdoptEaseUsage`,
    {
      workflow: workflowDetails,
    });
  return res.data;
};

export const fetchAdoptEaseConfig = async () => {
  const res = await bffInstance.get<{ data: IAdoptEaseConfig[] }>(`/adoptease/getAdoptEaseConfig`);
  return res.data;
};

export const fetchAdoptEaseCampaigns = async () => {
  const res = await bffInstance.get<IAdoptEaseCampaign[]>(`/adoptease/getAdoptEaseCampaigns`);
  return res.data;
};

export const fetchAdoptEaseUsageByUser = async () => {
  const res = await bffInstance.get<{ data: IAdoptEaseUsage[] }>(`/adoptease/getAdoptEaseUsageByUser`);
  return res.data;
};


export const checkAdoptEaseApplicationStatus = async (userId: string) => {
  const res = await bffInstance.post(`/adoptease/checkAdoptEaseApplicationStatus/${userId}`, 
    {
    oktaId: userId,
    });
  return res.data;
};

export const checkAdoptEaseDriveStatus = async (userId: string) => {
  const res = await bffInstance.post(`/adoptease/checkAdoptEaseDriveStatus/${userId}`, 
    {
    oktaId: userId,
    });
  return res.data;
};


export function useAdoptEaseConfig() {
  const adoptEaseConfig = useQuery({
    queryKey: ["getAdoptEaseConfig"],
    queryFn: () => fetchAdoptEaseConfig(),
    enabled: true
  });
  return adoptEaseConfig;
}

export function useAdoptEaseCampaigns() {
  const setAdoptEaseCampaigns = useAdoptEaseStore((state) => state.setAdoptEaseCampaigns);
  const adoptEaseCampaigns = useQuery({
    queryKey: ["getAdoptEaseCampaigns"],
    queryFn: () => fetchAdoptEaseCampaigns(),
    enabled: true,
    onSuccess: (data) => setAdoptEaseCampaigns(data ?? []),
  });
  return adoptEaseCampaigns;
}

export function useAdoptEaseUsageByUser() {
  const adoptEaseConfig = useQuery({
    queryKey: ["getAdoptEaseUsageByUser"],
    queryFn: () => fetchAdoptEaseUsageByUser(),
    enabled: true
  });
  return adoptEaseConfig;
}


export function useUpdateUserAdoptEaseUsage() {
  const queryClient = useQueryClient();

  const { data, mutate } = useMutation(
    (workflowDetails: Record<string, Date>) => updateUserAdoptEaseUsage(workflowDetails),
    {
      onMutate: async () => {
        await queryClient.cancelQueries({ queryKey: ['getAdoptEaseUsageByUser'] });
        const adoptEaseUsage = queryClient.getQueryData(
          'getAdoptEaseUsageByUser'
        ) as IAdoptEaseUsage[];
        queryClient.setQueryData('getAdoptEaseUsageByUser', adoptEaseUsage);
        return { adoptEaseUsage };
      },
      onError: (_error, _data, context) => {
        queryClient.setQueryData('getAdoptEaseUsageByUser', context?.adoptEaseUsage);
      },
      onSettled: () => {
        queryClient.invalidateQueries('getAdoptEaseUsageByUser');
      },
    }
  );
  return { data, mutate };
}


